/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { concat, Observable, of } from 'rxjs';
import { catchError, map, toArray } from 'rxjs/operators';

export interface BulkItem {
  id: string;
  name: string;
}

export interface BulkOutcome {
  ok: string[];
  failed: { id: string; name: string; reason: string }[];
}

/** What to tell the person once a bulk action has run: a level for the notification and its translation. */
export interface BulkReport {
  level: 'info' | 'warning' | 'error';
  key: string;
  params: Record<string, string | number>;
}

/**
 * Applies an operation to each item in turn and collects the outcome per item.
 *
 * Sequential rather than parallel: these are repository changes, and a partial
 * failure is much easier to explain when the order is deterministic. One item
 * failing never stops the rest.
 */
export function runEach<T extends BulkItem>(
  items: T[],
  operation: (item: T) => Observable<unknown>,
  reason: (error: unknown) => string
): Observable<BulkOutcome> {
  if (!items.length) {
    return of({ ok: [], failed: [] });
  }
  return concat(
    ...items.map((item) =>
      operation(item).pipe(
        toArray(),
        map(() => ({ id: item.id, name: item.name, reason: '' })),
        catchError((error) => of({ id: item.id, name: item.name, reason: reason(error) || '?' }))
      )
    )
  ).pipe(
    toArray(),
    map((results) => ({
      ok: results.filter((r) => !r.reason).map((r) => r.id),
      failed: results.filter((r) => r.reason)
    }))
  );
}

/** "Moved 3 items to the trash", "… 2 done, 1 failed: <first reason>", or "… failed: <reason>". */
export function bulkReport(outcome: BulkOutcome, action: string): BulkReport {
  if (!outcome.failed.length) {
    return { level: 'info', key: 'DMS.BULK.DONE', params: { action, count: outcome.ok.length } };
  }
  const first = outcome.failed[0];
  const reason = outcome.failed.length === 1 ? `${first.name}: ${first.reason}` : first.reason;
  if (!outcome.ok.length) {
    return { level: 'error', key: 'DMS.BULK.FAILED', params: { action, count: outcome.failed.length, reason } };
  }
  return { level: 'warning', key: 'DMS.BULK.PARTLY', params: { action, ok: outcome.ok.length, failed: outcome.failed.length, reason } };
}

/** An error the repository answers when the thing asked for is already so (already a favourite, already a member). */
export function isConflict(error: any): boolean {
  if (error?.status === 409 || error?.response?.status === 409 || error?.error?.statusCode === 409) {
    return true;
  }
  // the js-api hands the repository's answer over as the message's JSON
  try {
    return typeof error?.message === 'string' && JSON.parse(error.message)?.error?.statusCode === 409;
  } catch {
    return false;
  }
}

/**
 * The repository's own words for an error, without its log number: "Permission
 * denied" rather than `{"error":{"briefSummary":"06250012 Permission denied",…}}`.
 */
export function errorSummary(error: any): string {
  let text: string = error?.error?.error?.briefSummary ?? '';
  if (!text && typeof error?.message === 'string') {
    try {
      text = JSON.parse(error.message)?.error?.briefSummary ?? error.message;
    } catch {
      text = error.message;
    }
  }
  return String(text ?? '')
    .replace(/^\d{6,}\s+/, '')
    .trim();
}
